import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MenuItem } from '../../sidenav/sidenav.model';
import { TooltipDirective } from '../../tooltip/tooltip.directive';
import { SideMenuIconComponent } from '../side-menu-icon/side-menu-icon.component';
import { SideMenuItemComponent } from '../side-menu-item/side-menu-item.component';
import { SideMenuQuickActionComponent } from '../side-menu-quick-action/side-menu-quick-action.component';

// Same structural split as the item: the tooltip directive only exists while
// collapsed, so an expanded header never opens an empty bubble.
@Component({
  selector: 'lib-side-menu-group',
  imports: [
    NgTemplateOutlet,
    SideMenuItemComponent,
    SideMenuIconComponent,
    SideMenuQuickActionComponent,
    TooltipDirective,
  ],
  templateUrl: './side-menu-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuGroupComponent {
  readonly group = input.required<MenuItem>();
  readonly open = input<boolean>(false);
  readonly collapsed = input<boolean>(false);
  readonly toggle = output<void>();

  // The rail sits at the physical right edge in RTL; language changes reload the page, so reading dir once is enough.
  protected readonly tooltipPlacement = computed<'left' | 'right'>(() =>
    document.dir === 'rtl' ? 'left' : 'right'
  );
  protected readonly key = computed(
    () => this.group().id ?? this.group().label
  );
  protected readonly headerId = computed(() => `side_menu_group_${this.key()}`);
  protected readonly listId = computed(() => `${this.headerId()}_items`);
  protected readonly children = computed(() => this.group().children ?? []);
  // Expanded: px-md lines the label up with the item icons and the chevron sits at the row's end.
  // The header stays h-3xl with or without a quick action, matching the ＋ button.
  protected readonly headerClass = computed(() => {
    const base =
      'flex w-full items-center gap-x-sm rounded-sm text-xs text-neutral-500 hover:text-neutral-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ';
    if (this.collapsed()) {
      return base + 'h-xl justify-center';
    }
    return `${base}h-3xl min-w-0 flex-1 px-md`;
  });
  // Figma's chevron points up (open); it turns down while the group is closed.
  protected readonly chevronClass = computed(
    () => 'size-4 transition-transform' + (this.open() ? '' : ' rotate-180')
  );
}
